import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'react-toastify';
import { AlertCircle, ChevronLeft, Save, X } from 'react-feather';

import config from '../../../../config';
import useTenantLookups, { invalidateTenantLookups } from '../shared/useTenantLookups';
import BrandSection, { BRAND_FIELDS } from '../shared/BrandSection';
import UnitSection, { UNIT_FIELDS } from '../shared/UnitSection';
import TenancySection, { TENANCY_FIELDS } from '../shared/TenancySection';
import TenantProfileStrip from '../shared/TenantProfileStrip';
import TenantStatusModal from '../shared/TenantStatusModal';

import '../../../ControlPanel/sadmin.css';
import '../../Settings/settings.css';
import '../shared/tenant-form.css';

/**
 * /admin/tenant/tenantedit/:id — Edit Tenant, redesigned.
 *
 * Reuses the same Brand / Unit / Tenancy section components the Add
 * page uses, with two differences:
 * - Country / Property / Category / Subcategory render as read-only KV
 *   pairs (matched the V2 profile sidebar behaviour — these fields
 *   aren't editable post-creation).
 * - The Tenancy & charges section is mounted ONLY if the user has the
 *   `manageLeaseDetails` permission. When omitted, the section's
 *   fields never enter the form payload, satisfying the strict-gate
 *   requirement on lease editing.
 *
 * "Change status" button in the hero opens TenantStatusModal — saves
 * a status-route navigation. The `#tenancy` URL hash auto-scrolls to
 * the lease section (used by the /leaseedit/:id redirect).
 */
const READ_ONLY_FIELDS = ['tenant_country_id', 'tenant_property_id', 'tenant_category_id', 'tenant_subcategory_id'];

const TenantEdit = () => {
    const { t } = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();
    const tenancyRef = useRef(null);

    const manageTenants = localStorage.getItem('manageTenants');
    const manageGeneralSettings = localStorage.getItem('manageGeneralSettings');
    const manageLeaseDetails = localStorage.getItem('manageLeaseDetails');
    const canEdit = manageTenants === '1' || manageGeneralSettings === '1';
    const canEditLease = manageLeaseDetails === '1';
    // Page access: tenant-edit perm OR lease-edit perm. Preserves V2 behaviour
    // where a user with only manageLeaseDetails could reach the standalone
    // /leaseedit page (now folded in here as the Tenancy section).
    const canAccess = canEdit || canEditLease;
    // Lease-only mode: render the Tenancy section in isolation, hide
    // Brand + Unit sections, save only the lease payload.
    const leaseOnly = !canEdit && canEditLease;

    const loginId = localStorage.getItem('loginId');
    const loginName = localStorage.getItem('loginName');
    const token = localStorage.getItem('sessiontoken');
    const headers = useMemo(() => ({ Authorization: `${token}` }), [token]);

    const [tenant, setTenant] = useState(null);
    const [form, setForm] = useState({});
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const [statusModalOpen, setStatusModalOpen] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);

    const lookups = useTenantLookups({
        countryId: form.tenant_country_id,
        categoryId: form.tenant_category_id,
        propertyId: form.tenant_property_id,
    });

    // ── Auth ────────────────────────────────────────────────────────
    useEffect(() => {
        if (!canAccess) {
            window.alert(t('You are not authorized to view this page'));
            window.history.back();
        }
    }, [canAccess]);

    // ── Load tenant + assigned delivery partners ────────────────────
    // Two separate awaits: the delivery-partner endpoint returns 404 (not
    // 200 + []) when the tenant has no assigned partners, so a Promise.all
    // would reject and the whole page would fail to load. Treat 404 there
    // as an empty list.
    useEffect(() => {
        if (!id) return;
        let cancelled = false;
        const tenantP = axios.get(`${config.hostname}/tenant/${id}`, { headers });
        const partnersP = axios
            .post(`${config.hostname}/tenantdeliverypartner/getdeliverypartner`, { tenant_id: id }, { headers })
            .catch((err) => {
                if (err?.response?.status === 404) return { data: [] };
                throw err;
            });
        Promise.all([tenantP, partnersP])
            .then(([tenantRes, partnersRes]) => {
                if (cancelled) return;
                const t = tenantRes.data || {};
                setTenant(t);
                const assignedIds = (Array.isArray(partnersRes.data) ? partnersRes.data : [])
                    .map((p) => p.delivery_partner_id?._id)
                    .filter(Boolean);

                const dateStr = (d) => (d ? new Date(d).toISOString().split('T')[0] : '');

                setForm({
                    tenant_store_name: t.tenant_store_name || '',
                    tenant_brand_name: t.tenant_brand_name || '',
                    tenant_group_name: t.tenant_group_name || '',
                    tenant_email_id: t.tenant_email_id || '',
                    tenant_address: t.tenant_address || '',
                    tenant_country_id: t.tenant_country_id?._id || '',
                    tenant_property_id: t.tenant_property_id?._id || '',
                    tenant_franchisee: String(!!t.tenant_franchisee),
                    tenant_code: t.tenant_code || '',
                    tenant_unit_code: t.tenant_unit_code || '',
                    tenant_store_type: t.tenant_store_type || '',
                    tenant_current_status: t.tenant_current_status || '',
                    tenant_carpet_area: t.tenant_carpet_area ?? '',
                    tenant_chargable_area: t.tenant_chargable_area ?? '',
                    tenant_floor: t.tenant_floor || '',
                    tenant_location: t.tenant_location || '',
                    tenant_category_id: t.tenant_category_id?._id || '',
                    tenant_subcategory_id: t.tenant_subcategory_id?._id || '',
                    deliveryPartnersId: assignedIds,
                    tenant_contact_person: t.tenant_contact_person || '',
                    tenant_contact_number: t.tenant_contact_number || '',
                    tenant_it_contact_person: t.tenant_it_contact_person || '',
                    tenant_it_contact_number: t.tenant_it_contact_number || '',
                    tenant_pos_name: t.tenant_pos_name || '',
                    // Tenancy fields — only used if canEditLease.
                    tenant_lease_startdate: dateStr(t.tenant_lease_startdate),
                    tenant_lease_enddate: dateStr(t.tenant_lease_enddate),
                    tenant_base_rent: t.tenant_base_rent ?? '',
                    tenant_revenue_share: t.tenant_revenue_share ?? '',
                    tenant_revenue_share_method_id: t.tenant_revenue_share_method_id?._id || '',
                    tenant_charge1: t.tenant_charge1 ?? '',
                    tenant_charge2: t.tenant_charge2 ?? '',
                    tenant_charge3: t.tenant_charge3 ?? '',
                    tenant_charge4: t.tenant_charge4 ?? '',
                    tenant_charge5: t.tenant_charge5 ?? '',
                });
            })
            .catch((err) => {
                console.error('load tenant failed', err);
                toast.error(t('Failed to load tenant'));
            });
        return () => { cancelled = true; };
    }, [id, headers, refreshKey]);

    // ── #tenancy hash → scroll + flash ──────────────────────────────
    useEffect(() => {
        if (typeof window === 'undefined') return;
        if (window.location.hash === '#tenancy' && canEditLease && tenancyRef.current) {
            const t = setTimeout(() => {
                tenancyRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                tenancyRef.current?.classList.add('flash');
                setTimeout(() => tenancyRef.current?.classList.remove('flash'), 1700);
            }, 300);
            return () => clearTimeout(t);
        }
    }, [canEditLease, tenant]);

    // ── Cmd/Ctrl+S = save ──────────────────────────────────────────
    useEffect(() => {
        const onKey = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 's') {
                e.preventDefault();
                handleSubmit();
            }
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
        // form is captured by handleSubmit; intentional dep.
    }, [form]);

    // ── Form ops ────────────────────────────────────────────────────
    const onChange = useCallback((field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
        setErrors((prev) => {
            if (!prev[field]) return prev;
            const { [field]: _, ...rest } = prev;
            return rest;
        });
    }, []);

    const validate = () => {
        const next = {};
        // In lease-only mode only the Tenancy section is rendered, so only
        // its required fields participate in validation.
        const required = leaseOnly
            ? [...TENANCY_FIELDS.required]
            : [
                ...BRAND_FIELDS.required,
                ...UNIT_FIELDS.required,
                ...(canEditLease ? TENANCY_FIELDS.required : []),
            ];
        required.forEach((f) => {
            const v = form[f];
            if (v === undefined || v === '' || v === null) next[f] = t('Required');
        });
        if (!leaseOnly && form.tenant_store_type === 'Other' && !form.other_store_type) {
            next.other_store_type = t('Specify other store type');
        }
        if (canEditLease) {
            (lookups.charges || []).forEach((_, idx) => {
                const k = `tenant_charge${idx + 1}`;
                if (form[k] === undefined || form[k] === '') next[k] = t('Required');
            });
            if (form.tenant_lease_startdate && form.tenant_lease_enddate) {
                if (new Date(form.tenant_lease_enddate) <= new Date(form.tenant_lease_startdate)) {
                    next.tenant_lease_enddate = t('End date must be after start date');
                }
            }
        }
        setErrors(next);
        return next;
    };

    const handleSubmit = async () => {
        const errMap = validate();
        if (Object.keys(errMap).length) {
            // Scroll to the first invalid section.
            const target = Object.keys(errMap).some((k) => BRAND_FIELDS.all.includes(k))
                ? 'section-brand'
                : Object.keys(errMap).some((k) => UNIT_FIELDS.all.includes(k))
                    ? 'section-unit'
                    : 'section-tenancy';
            document.getElementById(target)?.scrollIntoView({ behavior: 'smooth' });
            return;
        }
        setSaving(true);
        try {
            // Build parallel requests. Each branch is gated by the
            // caller's permissions:
            //   - Brand + Unit fields go to /tenant/updatetenant/:id (gated
            //     by canEdit). Lease-only users skip this and the delivery
            //     partners update.
            //   - Lease fields go to /tenant/updatetenantlease/:id (gated
            //     by canEditLease). Strict — payload never includes lease
            //     fields when the permission is absent.
            const calls = [];
            if (!leaseOnly) {
                const generalPayload = {
                    tenant_country_id: form.tenant_country_id,
                    tenant_property_id: form.tenant_property_id,
                    tenant_category_id: form.tenant_category_id,
                    tenant_subcategory_id: form.tenant_subcategory_id,
                    tenant_group_name: form.tenant_group_name,
                    tenant_email_id: form.tenant_email_id,
                    tenant_address: form.tenant_address,
                    tenant_store_type: form.tenant_store_type === 'Other' ? form.other_store_type : form.tenant_store_type,
                    tenant_current_status: form.tenant_current_status,
                    tenant_carpet_area: form.tenant_carpet_area,
                    tenant_chargable_area: form.tenant_chargable_area,
                    tenant_floor: form.tenant_floor,
                    tenant_location: form.tenant_location,
                    tenant_franchisee: form.tenant_franchisee,
                    tenant_contact_person: form.tenant_contact_person,
                    tenant_contact_number: form.tenant_contact_number,
                    tenant_it_contact_person: form.tenant_it_contact_person,
                    tenant_it_contact_number: form.tenant_it_contact_number,
                    tenant_pos_name: form.tenant_pos_name,
                    tenant_modified_date: new Date().toISOString().substr(0, 10),
                    login_id: loginId,
                };
                calls.push(axios.post(`${config.hostname}/tenant/updatetenant/${id}`, generalPayload, { headers }));
            }
            if (canEditLease) {
                const leasePayload = {
                    tenant_lease_startdate: form.tenant_lease_startdate,
                    tenant_lease_enddate: form.tenant_lease_enddate,
                    tenant_base_rent: form.tenant_base_rent,
                    tenant_revenue_share: form.tenant_revenue_share,
                    tenant_revenue_share_method_id: form.tenant_revenue_share_method_id,
                    tenant_charge1: form.tenant_charge1 || 0,
                    tenant_charge2: form.tenant_charge2 || 0,
                    tenant_charge3: form.tenant_charge3 || 0,
                    tenant_charge4: form.tenant_charge4 || 0,
                    tenant_charge5: form.tenant_charge5 || 0,
                    login_id: loginId,
                };
                calls.push(axios.post(`${config.hostname}/tenant/updatetenantlease/${id}`, leasePayload, { headers }));
            }

            // 3. Delivery partners — same endpoint as V2. Skipped for
            //    lease-only users (the Unit section that hosts the picker
            //    isn't even rendered for them).
            if (!leaseOnly) {
                const deliveryPayload = {
                    concessionaire_id: tenant?.tenant_concessionaire_id?._id || tenant?.tenant_concessionaire_id,
                    tenant_id: id,
                    delivery_partner_id: form.deliveryPartnersId || [],
                    login_id: loginId,
                    tenant_store_name: form.tenant_store_name,
                    login_name: loginName,
                };
                calls.push(axios.post(`${config.hostname}/tenantdeliverypartner/updatedeliverypartner`, deliveryPayload, { headers }));
            }

            await Promise.all(calls);
            toast.success(
                leaseOnly
                    ? t('Lease updated')
                    : canEditLease
                        ? t('Tenant and lease updated')
                        : t('Tenant updated'),
            );
            invalidateTenantLookups();
            setRefreshKey((k) => k + 1);
        } catch (err) {
            console.error('update tenant failed', err);
            toast.error(t('Save failed'));
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => navigate(-1);

    if (!canAccess || !tenant) {
        return canAccess ? <div className="v3-sadmin"><div className="v3-sadmin-empty"><p>{t('Loading…')}</p></div></div> : null;
    }

    const errorCount = Object.keys(errors).length;

    return (
        <Fragment>
            <div className="v3-sadmin">
                <div className="v3-sadmin-hero">
                    <div className="v3-sadmin-hero-text">
                        <p className="v3-label" style={{ margin: 0, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            <ChevronLeft size={12} />
                            <span
                                style={{ cursor: 'pointer' }}
                                onClick={() => navigate(`${process.env.PUBLIC_URL}/admin/tenant/viewtenant/default`)}
                            >
                                {t('Tenants')}
                            </span>
                        </p>
                        <h1 className="v3-display v3-sadmin-greeting">
                            <span className="v3-sadmin-name">{leaseOnly ? t('Edit lease') : t('Edit tenant')}</span>
                        </h1>
                        <p className="v3-body v3-sadmin-sub">
                            {leaseOnly
                                ? t('Update the lease and charge details for this tenant.')
                                : canEditLease
                                    ? t('Update brand, unit and tenancy details for this tenant.')
                                    : t('Update brand, unit details for this tenant.')}
                        </p>
                    </div>
                    <div className="v3-sadmin-hero-actions">
                        {canEdit && (
                            <button type="button" className="v3-btn v3-btn-ghost" onClick={() => setStatusModalOpen(true)}>
                                {t('Change status')}
                            </button>
                        )}
                    </div>
                </div>

                <TenantProfileStrip
                    tenant={tenant}
                    onChangeStatus={canEdit ? () => setStatusModalOpen(true) : undefined}
                />

                {errorCount > 0 && (
                    <div className="v3-tenant-error-banner">
                        <AlertCircle size={16} />
                        <span>{errorCount === 1 ? t('{{count}} field need attention.', { count: errorCount }) : t('{{count}} fields need attention.', { count: errorCount })}</span>
                    </div>
                )}

                <div className="v3-tenant-form-col">
                    {/* Brand + Unit only render when the user can actually edit
                        those fields. Lease-only users see just the Tenancy
                        section, matching the V2 standalone LeaseEdit page. */}
                    {!leaseOnly && (
                        <>
                            <BrandSection
                                mode="edit"
                                form={form}
                                errors={errors}
                                onChange={onChange}
                                lookups={lookups}
                                readOnly={READ_ONLY_FIELDS}
                            />
                            <UnitSection
                                mode="edit"
                                form={form}
                                errors={errors}
                                onChange={onChange}
                                lookups={lookups}
                                readOnly={READ_ONLY_FIELDS}
                            />
                        </>
                    )}
                    {canEditLease && (
                        <div ref={tenancyRef}>
                            <TenancySection
                                mode="edit"
                                form={form}
                                errors={errors}
                                onChange={onChange}
                                lookups={lookups}
                            />
                        </div>
                    )}
                </div>
            </div>

            <div className="v3-tenant-action-bar">
                <span className="v3-tenant-action-summary">
                    {saving ? t('Saving…') : errorCount > 0 ? (errorCount === 1 ? t('{{count}} field need attention', { count: errorCount }) : t('{{count}} fields need attention', { count: errorCount })) : t('Ready to save')}
                </span>
                <button type="button" className="v3-btn v3-btn-ghost" onClick={handleCancel} disabled={saving}>
                    <X size={14} /> {t('Cancel')}
                </button>
                <button type="button" className="v3-btn v3-btn-primary" onClick={handleSubmit} disabled={saving}>
                    <Save size={14} /> {saving ? t('Saving…') : t('Save changes')}
                </button>
            </div>

            {statusModalOpen && (
                <TenantStatusModal
                    tenantId={id}
                    onClose={() => setStatusModalOpen(false)}
                    onSaved={() => setRefreshKey((k) => k + 1)}
                />
            )}
        </Fragment>
    );
};

export default TenantEdit;
